import { useState, useEffect, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import { AuthProvider, useAuth } from "@/components/AuthContext";
import { ThemeProvider, useTheme } from "@/components/ThemeContext";
import AuthPage from "@/components/AuthPage";
import Sidebar from "@/components/Sidebar";
import ChatView from "@/components/ChatView";
import AdminView from "@/components/AdminView";
import PlansView from "@/components/PlansView";
import WidgetView from "@/components/WidgetView";
import ResetPasswordPage from "@/components/ResetPasswordPage";
import { AnimatePresence, motion } from "framer-motion";
import { Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function LoadingScreen() {
  return (
    <div className="h-screen flex items-center justify-center" style={{ backgroundColor: "var(--bg-main)" }}>
      <div className="text-center">
        <Loader2 size={28} className="text-emerald-400 animate-spin mx-auto mb-3" />
        <p className="text-xs" style={{ color: "var(--text-muted)" }}>Cargando...</p>
      </div>
    </div>
  );
}

function MainApp() {
  const { user, loading: authLoading, logout } = useAuth();
  const [view, setView] = useState("chat"); // "chat", "plans", "admin"
  const [sessions, setSessions] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const isAdmin = user && user.role === "admin";

  const fetchSessions = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/chat/sessions`);
      setSessions(res.data);
    } catch (e) {
      console.error("Error fetching sessions", e);
    }
  }, []);

  const fetchMessages = useCallback(async (sessionId) => {
    try {
      const res = await axios.get(`${API}/chat/messages/${sessionId}`);
      setMessages(res.data);
    } catch (e) {
      console.error("Error fetching messages", e);
    }
  }, []);

  useEffect(() => {
    if (user) fetchSessions();
  }, [user, fetchSessions]);

  useEffect(() => {
    if (activeSessionId) {
      fetchMessages(activeSessionId);
    } else {
      setMessages([]);
    }
  }, [activeSessionId, fetchMessages]);

  // If student tries to go to admin, redirect to chat
  useEffect(() => {
    if (view === "admin" && !isAdmin) {
      setView("chat");
    }
  }, [view, isAdmin]);

  if (authLoading) return <LoadingScreen />;
  if (!user) return <AuthPage />;

  const createSession = async () => {
    try {
      const res = await axios.post(`${API}/chat/sessions`);
      setSessions((prev) => [res.data, ...prev]);
      setActiveSessionId(res.data.id);
      setView("chat");
      // Fetch messages in case there's a welcome message
      const msgs = await axios.get(`${API}/chat/messages/${res.data.id}`);
      setMessages(msgs.data);
    } catch (e) {
      console.error("Error creating session", e);
    }
  };

  const deleteSession = async (sessionId) => {
    try {
      await axios.delete(`${API}/chat/sessions/${sessionId}`);
      setSessions((prev) => prev.filter((s) => s.id !== sessionId));
      if (activeSessionId === sessionId) {
        setActiveSessionId(null);
        setMessages([]);
      }
    } catch (e) {
      console.error("Error deleting session", e);
    }
  };

  const sendMessage = async (text, fromVoice = false) => {
    if (!text.trim() || loading) return;

    let sessionId = activeSessionId;
    if (!sessionId) {
      try {
        const res = await axios.post(`${API}/chat/sessions`);
        sessionId = res.data.id;
        setSessions((prev) => [res.data, ...prev]);
        setActiveSessionId(sessionId);
      } catch (e) {
        console.error("Error creating session", e);
        return;
      }
    }

    const tempUserMsg = {
      id: "temp-" + Date.now(),
      session_id: sessionId,
      role: "user",
      content: text,
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, tempUserMsg]);
    setLoading(true);

    try {
      const res = await axios.post(`${API}/chat/send`, {
        session_id: sessionId,
        message: text,
      });
      setMessages((prev) => {
        const filtered = prev.filter((m) => m.id !== tempUserMsg.id);
        return [...filtered, res.data.user_message, res.data.assistant_message];
      });
      fetchSessions();

      // Auto-play response with voice if message was sent by voice
      if (fromVoice && res.data.assistant_message?.content) {
        try {
          const ttsRes = await axios.post(`${API}/tts/generate`, {
            text: res.data.assistant_message.content,
          });
          if (ttsRes.data.audio) {
            const b64 = ttsRes.data.audio.split(",")[1];
            const binary = atob(b64);
            const bytes = new Uint8Array(binary.length);
            for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
            const blob = new Blob([bytes], { type: "audio/mpeg" });
            const url = URL.createObjectURL(blob);
            const audio = new Audio();
            audio.src = url;
            audio.onended = () => URL.revokeObjectURL(url);
            await audio.play();
          }
        } catch (e) {
          // TTS not available, silent fail
        }
      }
    } catch (e) {
      console.error("Error sending message", e);
      setMessages((prev) => prev.filter((m) => m.id !== tempUserMsg.id));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex h-screen overflow-hidden" data-testid="main-app" style={{ backgroundColor: "var(--bg-main)", color: "var(--text-primary)" }}>
      <Sidebar
        sessions={sessions}
        activeSessionId={activeSessionId}
        onSelectSession={(id) => {
          setActiveSessionId(id);
          setView("chat");
        }}
        onNewChat={createSession}
        onDeleteSession={deleteSession}
        view={view}
        onViewChange={setView}
        isOpen={sidebarOpen}
        onToggle={() => setSidebarOpen(!sidebarOpen)}
        user={user}
        onLogout={logout}
        isAdmin={isAdmin}
      />
      <main className="flex-1 flex flex-col min-w-0">
        <AnimatePresence mode="wait">
          {view === "chat" ? (
            <motion.div
              key="chat"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              className="flex-1 flex flex-col min-h-0"
            >
              <ChatView
                messages={messages}
                onSend={sendMessage}
                loading={loading}
                activeSessionId={activeSessionId}
                onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
                sidebarOpen={sidebarOpen}
              />
            </motion.div>
          ) : view === "plans" ? (
            <motion.div
              key="plans"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              className="flex-1 flex flex-col min-h-0"
            >
              <PlansView />
            </motion.div>
          ) : (
            <motion.div
              key="admin"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              className="flex-1 flex flex-col min-h-0"
            >
              <AdminView
                onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
                sidebarOpen={sidebarOpen}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/widget" element={<WidgetView />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route
          path="/*"
          element={
            <ThemeProvider>
              <AuthProvider>
                <MainApp />
              </AuthProvider>
            </ThemeProvider>
          }
        />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
